<template>
  <teleport to="body">
    <transition name="overlay-fade" appear>
      <div
        v-if="visible"
        class="custom-confirm-overlay"
        :style="{ zIndex }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="headingId"
        @click="handleOverlayClick"
      >
        <transition name="dialog-pop" appear mode="out-in">
          <div
            class="custom-confirm-container"
            :style="{ width }"
            @click.stop
            key="dialog"
          >
            <div class="confirm-header">
              <h3 class="confirm-title" :id="headingId">{{ title }}</h3>
            </div>

            <div class="confirm-body">
              <div class="confirm-icon" :class="`icon-${type}`">
                <svg
                  v-if="type === 'warning'"
                  viewBox="0 0 24 24"
                  class="icon-svg"
                  aria-hidden="true"
                >
                  <path
                    d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                  />
                </svg>
                <svg
                  v-else-if="type === 'info'"
                  viewBox="0 0 24 24"
                  class="icon-svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                  />
                </svg>
                <svg
                  v-else-if="type === 'success'"
                  viewBox="0 0 24 24"
                  class="icon-svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  class="icon-svg"
                  aria-hidden="true"
                >
                  <path
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </div>
              <p class="confirm-message">{{ message }}</p>
            </div>

            <div class="confirm-footer">
              <CustomButton type="default" size="small" @click="handleCancel">
                {{ cancelButtonTextResolved }}
              </CustomButton>
              <CustomButton type="primary" size="small" @click="handleConfirm">
                {{ confirmButtonTextResolved }}
              </CustomButton>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed } from "vue";
import CustomButton from "./CustomButton.vue";

const emit = defineEmits(["confirm", "cancel"]);

const props = defineProps({
  visible: { type: Boolean, default: false },
  title: { type: String, default: "提示" },
  message: { type: String, default: "" },
  type: {
    type: String,
    default: "warning",
    validator: (v) => ["warning", "info", "success", "error"].includes(v),
  },
  confirmButtonText: { type: String, default: "确定" },
  cancelButtonText: { type: String, default: "取消" },
  closeOnClickModal: { type: Boolean, default: true },
  width: { type: String, default: "420px" },
  zIndex: { type: Number, default: 30000 },
});

const headingId = `ccd-${Math.random().toString(36).slice(2)}`;

const confirmButtonTextResolved = computed(
  () => props.confirmButtonText || "确定",
);
const cancelButtonTextResolved = computed(
  () => props.cancelButtonText || "取消",
);

const handleConfirm = () => {
  emit("confirm");
};

const handleCancel = () => {
  emit("cancel");
};

const handleOverlayClick = () => {
  if (props.closeOnClickModal) handleCancel();
};
</script>

<style scoped lang="less">
.custom-confirm-overlay {
  --milk-overlay: rgba(248, 244, 237, 0.55);
  --milk-card: #3b3b3b;
  --milk-card-2: #fffefb;
  --milk-text: #f0ecdf;
  --milk-muted: #6e6a64;
  --milk-border: rgba(39, 33, 24, 0.08);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--milk-overlay);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: overlayIn 0.5s ease;

  .custom-confirm-container {
    background: var(--milk-card);
    border: 1px solid var(--milk-border);
    border-radius: 16px;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.3);
    min-width: 360px;
    max-width: 92vw;
    color: var(--milk-text);

    /* smoothness helpers */
    will-change: transform, opacity, filter;
    transform-origin: 50% 48%;
    backface-visibility: hidden;
    animation: dialogIn 0.5s ease;

    .confirm-header {
      padding: 18px 20px 12px;
      text-align: center;

      .confirm-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0.2px;
        color: var(--milk-text);
      }
    }

    .confirm-body {
      padding: 12px 20px 8px;
      display: flex;
      align-items: center;
      gap: 14px;

      .confirm-icon {
        flex-shrink: 0;
        width: 38px;
        height: 38px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--milk-border);
        background: #fffefb;

        .icon-svg {
          width: 20px;
          height: 20px;
          fill: currentColor;
        }

        &.icon-warning {
          background: #fff8e6;
          border-color: #f7e7ba;
          color: #b98507;
        }
        &.icon-info {
          background: #eef6ff;
          border-color: #cde3fb;
          color: #2a72c7;
        }
        &.icon-success {
          background: #f1fbf0;
          border-color: #d0f0cb;
          color: #2f8a1f;
        }
        &.icon-error {
          background: #ffeceb;
          border-color: #f7c9c6;
          color: #c93b35;
        }
      }

      .confirm-message {
        flex: 1;
        margin: 0;
        font-size: 14.5px;
        color: var(--milk-text);
        line-height: 1.7;
      }
    }

    .confirm-footer {
      padding: 14px 20px 18px;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      position: relative;
    }

    .confirm-footer::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0),
        var(--milk-border),
        rgba(0, 0, 0, 0)
      );
    }
  }
}

/* ========= Overlay fade (gentler) ========= */
/* Slightly longer and softer curves */
.overlay-fade-enter-active {
  transition: opacity 260ms cubic-bezier(0.16, 1, 0.3, 1);
}
.overlay-fade-leave-active {
  transition: opacity 240ms ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  filter: blur(2px);
}

@keyframes overlayIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes dialogIn {
  from {
    transform: translateY(-50px);
  }
  to {
    transform: translateY(0);
  }
}
</style>
